import React, { type ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { LucideIcon } from "lucide-react";

export interface EmptyStateProps {
  /** Lucide icon component (e.g. `Puzzle`), rendered in an accent medallion. */
  icon: LucideIcon;
  /** Short headline ("No skills yet"). */
  title: string;
  /** Supporting description / guidance. */
  description?: ReactNode;
  /** Optional call-to-action (a Button) shown beneath the description. */
  action?: ReactNode;
}

/**
 * Unified empty-state for workspace lists. Replaces the dashed-border stubs
 * (and bare `Typography` fallbacks) each page rolled separately, giving every
 * page an icon medallion + title + description + optional CTA. Uses
 * role="status" so screen readers announce the empty result.
 */
export function EmptyState({ icon: Icon, title, description, action }: EmptyStateProps) {
  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        py: 7,
        px: 3,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 56,
          height: 56,
          mb: 2,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          color: "primary.main",
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        }}
      >
        <Icon size={26} />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}
